import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { formatNotificationTime } from '@/lib/notifications/format-relative-time';
import { useIsDesktop } from '@/lib/notifications/useIsDesktop';
import { useNotificationActions } from '@/lib/notifications/useNotificationActions';
import {
  useDismissIncomingNotification,
  useIncomingNotification,
  useOpenNotificationDetail,
} from '@/store/notificationsStore';
import { NotificationIcon } from './NotificationIcon';

/** How long the banner lingers before auto-snoozing. */
const AUTO_SNOOZE_MS = 5000;
/** Matches the `notif-banner-out` animation duration. */
const EXIT_MS = 250;
/** Desktop fallback top offset when the bell isn't in the DOM (matches the pinned bar height). */
const FALLBACK_TOP_PX = 64;

/** Pull a string field off the loosely-typed `meta` payload, if present. */
function metaString(meta: Record<string, unknown>, key: string): string | undefined {
  const value = meta[key];
  return typeof value === 'string' ? value : undefined;
}

/**
 * Pop-in banner for a just-arrived notification (paired with the bell's ring —
 * both are driven by the store via {@link useNotificationsRealtime}). It grows
 * out of the bell's corner (top-right transform origin) and stretches to the
 * full page width. Positioning splits by form factor: on desktop the fixed
 * row tracks just under the bell's live position, right-anchored and capped
 * at `max-w-xl`; on mobile it pins to the top of the window with a small
 * margin, full-width.
 *
 * Clicking it marks the notification read and opens the standard detail
 * pop-up; otherwise it auto-snoozes after 5s (hover holds it). Mounted once at
 * the app root, above the top bar (z-30) and under modals/sheets (z-50).
 */
export function IncomingNotificationBanner() {
  const incoming = useIncomingNotification();
  const dismiss = useDismissIncomingNotification();
  const openDetail = useOpenNotificationDetail();
  const { markRead } = useNotificationActions();
  const isDesktop = useIsDesktop();

  const [exiting, setExiting] = useState(false);
  const [hovered, setHovered] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);

  // Render-phase state adjustment (react.dev/learn/you-might-not-need-an-effect):
  // a newer arrival cancels any in-flight exit so the entrance replays cleanly.
  const [lastShownId, setLastShownId] = useState<string | null>(null);
  if (incoming && incoming.id !== lastShownId) {
    setLastShownId(incoming.id);
    setExiting(false);
  }

  // Desktop: pin the row just under wherever the bell currently sits and keep
  // tracking it while visible — the top bar scrolls away then re-pins, so the
  // bell's viewport position changes under the banner. rAF-throttled direct
  // style writes (no state), same pattern as HomeTopBar's stick tracking.
  // Mobile: no tracking — the row stays at the window top (top-2 class).
  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row || !incoming) return;
    if (!isDesktop) {
      row.style.top = '';
      return;
    }

    let scheduled = false;
    const place = () => {
      scheduled = false;
      const rect = document.querySelector('[data-testid="notification-bell"]')?.getBoundingClientRect();
      row.style.top = `${rect && rect.bottom > 0 ? Math.round(rect.bottom + 8) : FALLBACK_TOP_PX}px`;
    };
    const onScroll = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(place);
    };

    place();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [incoming, isDesktop]);

  // Auto-snooze after the linger, holding while hovered (the timer restarts in
  // full on mouse-leave — fine for a 5s window).
  useEffect(() => {
    if (!incoming || hovered || exiting) return;
    const timer = setTimeout(() => setExiting(true), AUTO_SNOOZE_MS);
    return () => clearTimeout(timer);
  }, [incoming, hovered, exiting]);

  // Let the exit animation play before the store drops the notification.
  useEffect(() => {
    if (!exiting) return;
    const timer = setTimeout(() => {
      dismiss();
      setExiting(false);
    }, EXIT_MS);
    return () => clearTimeout(timer);
  }, [exiting, dismiss]);

  if (!incoming) return null;

  const handleOpen = () => {
    markRead(incoming);
    openDetail(incoming);
    dismiss();
    setExiting(false);
  };

  return (
    <div
      ref={rowRef}
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed inset-x-3 top-2 z-40 flex justify-end md:inset-x-6 lg:inset-x-8 lg:top-16"
    >
      <button
        key={incoming.id}
        type="button"
        onClick={handleOpen}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        data-testid="incoming-notification-banner"
        className={cn(
          'pointer-events-auto flex w-full max-w-xl items-center gap-3 rounded-card border border-border bg-surface px-4 py-3 text-left shadow-lg',
          exiting ? 'animate-notif-banner-out' : 'animate-notif-banner-in',
        )}
      >
        <NotificationIcon type={incoming.type} imageUrl={metaString(incoming.meta, 'marketImageUrl')} />
        {/* Push-style: icon + TITLE only (no body line) — matches the Figma banner. */}
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-primary">{incoming.title}</span>
        <time className="shrink-0 text-xs text-muted" dateTime={incoming.createdAt}>
          {formatNotificationTime(incoming.createdAt)}
        </time>
      </button>
    </div>
  );
}
